import { useEffect, useState } from 'react'
import Uppy from '@uppy/core'
import XHRUpload from '@uppy/xhr-upload'

import { baseURL } from '@functions/api'
import { getAccessToken } from '@functions/accessToken'

import type { SuccessResponse } from '@uppy/core'
import type { EntityOptions } from '@redux/modules/module'

type UseUppyEntityAssetUploadOptions = {
  allowedFileTypes?: string,
  callbacks?: {
    afterAction?: (response: SuccessResponse) => void,
  },
  endpoint: string,
  fieldName: string,
  requestOptions?: EntityOptions,
}

const initializeUppy = (options: UseUppyEntityAssetUploadOptions) => {
  const {
    allowedFileTypes,
    callbacks,
    endpoint,
    fieldName,
    requestOptions = {},
  } = options

  const { afterAction } = callbacks || {}

  const accessToken = getAccessToken()

  const uppy = new Uppy({
    debug: false,
    autoProceed: true,
    restrictions: {
      allowedFileTypes: allowedFileTypes ? allowedFileTypes.split(',') : ['image/*'],
    },
  })
    .use(XHRUpload, {
      endpoint: `${baseURL}${endpoint}`,
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      formData: true,
      // fieldName: fieldName || 'image[file]',
      fieldName,
    })
    .on('file-added', (file) => {
      uppy.setFileMeta(file.id, {
        ...requestOptions,
      })
    })
    .on('upload-success', (_, response) => {
      if (afterAction) afterAction(response)
    })

  return uppy
}

const checkRequiredOptions = (options = {}) => {
  const requiredOptions = ['endpoint', 'fieldName']
  const optionKeys = Object.keys(options)

  return requiredOptions.every(key => optionKeys.includes(key))
}

function useUppyEntityAssetUpload(
  options: UseUppyEntityAssetUploadOptions,
  deps: string[] = [],
) {
  const [uppy, setUppy] = useState<Uppy | undefined>(undefined)

  useEffect(() => {
    if (checkRequiredOptions(options)){
      return setUppy(initializeUppy(options))
    }

    return () => {
      if (uppy){
        uppy.close()
        setUppy(undefined)
      }
    }
  }, [...deps])

  return uppy
}

export default useUppyEntityAssetUpload
